// TODO: CSS-3837 delete this file once the reworked spousal variable benefit endpoints are deployed.

import {
  IpSpousalVariableBenefitSourceAccounts,
  SpousalVariableBenefitSourceAccount
} from '../features/spousal-variable-benefit-rollover/models/ip-spousal-variable-benefit-source-accounts';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { RelationshipStatus } from '../models/enum/shared/relationship-status';
import { MemberAccountStatusIdEnum } from '../models/transactions/enum/member-account-status-id.enum';
import { MemberAccount, SpousalRolloverAccount } from '../models/transactions/member-account';
import { MemberRecord } from '../models/users/dto/member-record';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { VariableBenefitAccountDetails } from '../models/transactions/variable-benefit-account-details';
import { VbAccountActivityBreakdown } from '../models/transactions/vb-account-activity-breakdown';
import { MemberAccountFundAllocation } from '../models/transactions/member-account-fund-allocation';
import { InvestmentInstructions } from '../models/transactions/investment-instructions';
import { VbAnnualDetailsProvenanceTypeEnum } from '../models/enum/shared/vb-annual-details-provenance-type.enum';
import { DateUtils } from '../utils/date-utils';
import { currentVbPlanYear } from '../views/shared/components/variable-benefit-accounts/utils/vb-annual-details-utils';

const ACTIVE_SPOUSE_STATUSES = [RelationshipStatus.Married, RelationshipStatus.CommonLaw, RelationshipStatus.Separated];
const ROLLOVER_ACCOUNT_STATUSES = [MemberAccountStatusIdEnum.Active, MemberAccountStatusIdEnum.Suspended];

const spousalRolloversBySourceAccountId = new Map<number, SpousalRolloverAccount>();
const createdSpousalAccounts: MemberAccount[] = [];

export function mockGetSpousalVariableBenefitSourceAccounts(
  member: MemberRecord,
  accounts: MemberAccount[]
): IpSpousalVariableBenefitSourceAccounts {
  const relationshipStatus = member.activeRelationshipStatus;
  const spouse = relationshipStatus?.spouse;
  if (!spouse || !ACTIVE_SPOUSE_STATUSES.includes(relationshipStatus.typeId)) {
    return window.injector.Deserialize.instanceOf(IpSpousalVariableBenefitSourceAccounts, {
      memberId: member.id,
      activeSpouse: null,
      accounts: []
    });
  }

  const spouseName = `${spouse.firstName} ${spouse.lastName}`;
  const year = new Date().getFullYear();
  const sourceAccounts = accounts
    .filter(
      account =>
        account.typeId === AccountTypeIdEnum.VariableBenefit &&
        ROLLOVER_ACCOUNT_STATUSES.includes(account.statusId) &&
        !account.variableBenefitAccountDetails?.isSpousal &&
        !spousalRolloversBySourceAccountId.has(account.id)
    )
    .map(account => {
      const details = account.variableBenefitAccountDetails;
      const annualDetails = details?.currentYearAnnualDetails;
      return {
        accountId: account.id,
        idPrefix: 'VB',
        governingJurisdictionId: account.governingJurisdictionId ?? null,
        governingJurisdiction: account.governingJurisdiction?.name ?? null,
        createdDate: account.createdDate ?? new Date().toISOString(),
        specifiedBeneficiary: { name: spouseName, memberId: null },
        minimumAge: {
          name: annualDetails?.minimumAgeName ?? spouseName,
          dateOfBirth: annualDetails?.minimumAgeDateOfBirth ?? spouse.birthDate,
          isSpouse: !annualDetails?.minimumAgeName
        },
        effectiveYear: year,
        minimumAnnualWithdrawalAmount: annualDetails?.minimumAnnualWithdrawalAmount ?? 3500,
        maximumAnnualWithdrawalAmount: annualDetails?.maximumAnnualWithdrawalAmount ?? 7500,
        struckOnBalance: 618750,
        struckOnDate: `${year}-01-01`,
        yearToDatePaymentAmount: details?.yearToDatePaymentAmount ?? 0
      };
    });

  return window.injector.Deserialize.instanceOf(IpSpousalVariableBenefitSourceAccounts, {
    memberId: member.id,
    activeSpouse: { spouseId: spouse.id, name: spouseName, linkedMemberId: null },
    accounts: sourceAccounts
  });
}

export function mockCreateSpousalVariableBenefitAccounts(
  sourceMember: MemberRecord,
  successor: MemberUserSearchResult,
  sourceAccounts: SpousalVariableBenefitSourceAccount[]
): void {
  sourceAccounts.forEach(sourceAccount => {
    const account = window.injector.Deserialize.instanceOf(MemberAccount, {
      id: 910000000 + createdSpousalAccounts.length,
      memberId: successor.id,
      member: successor.name,
      typeId: AccountTypeIdEnum.VariableBenefit,
      type: { id: AccountTypeIdEnum.VariableBenefit, name: 'Variable Benefit Account', idPrefix: 'VB' },
      statusId: MemberAccountStatusIdEnum.Pending,
      status: { id: MemberAccountStatusIdEnum.Pending, name: 'Pending' },
      createdDate: new Date().toISOString(),
      governingJurisdictionId: sourceAccount.governingJurisdictionId,
      governingJurisdiction: sourceAccount.governingJurisdictionId
        ? { id: sourceAccount.governingJurisdictionId, name: sourceAccount.governingJurisdiction }
        : null,
      totalBalance: 0,
      spousalRolloverSource: {
        sourceAccountId: sourceAccount.accountId,
        sourceAccountIdPrefix: sourceAccount.idPrefix,
        sourceMemberId: sourceMember.id,
        sourceMemberName: sourceMember.fullName,
        sourceAccountStatusId: MemberAccountStatusIdEnum.Active
      }
    });
    createdSpousalAccounts.push(account);
    spousalRolloversBySourceAccountId.set(sourceAccount.accountId, {
      spousalAccountId: account.id,
      spousalAccountIdPrefix: 'VB',
      successorMemberId: successor.id,
      successorName: successor.name,
      spousalAccountStatusId: MemberAccountStatusIdEnum.Pending
    });
  });
}

export function mockAttachSpousalRollovers(
  memberId: number,
  accounts: MemberAccount[],
  accountIds?: number[]
): MemberAccount[] {
  const created = createdSpousalAccounts.filter(
    account => account.memberId === memberId && (!accountIds?.length || accountIds.includes(account.id))
  );
  return [...accounts, ...created].map(mockAttachSpousalRolloverAccount);
}

export function mockAttachSpousalRolloverAccount(account: MemberAccount): MemberAccount {
  const spousalRolloverAccount = spousalRolloversBySourceAccountId.get(account.id);
  return spousalRolloverAccount ? Object.assign(new MemberAccount(), account, { spousalRolloverAccount }) : account;
}

export function mockFindCreatedSpousalAccount(accountId: number): MemberAccount | null {
  return createdSpousalAccounts.find(account => account.id === accountId) ?? null;
}

export function mockGetSpousalVariableBenefitAccountDetails(account: MemberAccount): VariableBenefitAccountDetails {
  return window.injector.Deserialize.instanceOf(VariableBenefitAccountDetails, {
    accountId: account.id,
    isSpousal: true,
    governingJurisdictionId: account.governingJurisdictionId,
    governingJurisdiction: account.governingJurisdiction,
    effectiveDate: DateUtils.todayInCST(),
    totalBalance: 0,
    yearToDatePaymentAmount: 0,
    currentYearAnnualDetails: {
      effectiveYear: currentVbPlanYear(),
      provenanceTypeId: VbAnnualDetailsProvenanceTypeEnum.Actual,
      minimumAnnualWithdrawalAmount: 0,
      maximumAnnualWithdrawalAmount: 0,
      maximumRaisedToMinimumAmount: 0
    },
    nextYearAnnualDetails: null,
    historicalLumpSumPayments: [],
    withdrawalInstructions: []
  });
}

export function mockGetSpousalVariableBenefitActivityBreakdown(): VbAccountActivityBreakdown {
  return window.injector.Deserialize.instanceOf(VbAccountActivityBreakdown, {
    openingBalance: 0,
    depositsAndTransfersIn: 0,
    earnings: 0,
    payments: 0,
    other: 0,
    currentBalance: 0
  });
}

export function mockGetSpousalVariableBenefitFundAllocation(): MemberAccountFundAllocation[] {
  return MemberAccountFundAllocation.generateFakes(true).value;
}

export function mockGetSpousalVariableBenefitInvestmentInstructions(account: MemberAccount): InvestmentInstructions[] {
  const instructions = mockGetSpousalVariableBenefitFundAllocation().map(({ fundId, fund }) => ({
    fundId,
    fund,
    percentage: fund.isBalanced() ? 100 : 0
  }));
  return [
    window.injector.Deserialize.instanceOf(InvestmentInstructions, {
      id: account.id,
      accountDetails: {
        accountId: account.id,
        typeId: account.typeId,
        type: account.type,
        status: account.status,
        statusId: account.statusId,
        totalBalance: 0
      },
      lastUpdatedDate: null,
      investmentInstructions: instructions
    })
  ];
}
